import type { SnAudioValue } from "@shared/components/SnAudioRecorder";
import { global } from "@shared/libs/Global";

/**
 * 목적: SnAudioRecorder가 만든 실제 음성 blob을 화면(SN_SFA_MEETING_REG_001)까지 전달하기 위한 경량 보관소.
 *
 * 배경:
 * SharedForm → SnFindFormContols 는 메타 기반 렌더러라, 오디오 컨트롤이 Blob 을 grid row 에 실을 수 없다.
 * 전사(STT)와 저장 업로드에는 실제 Blob이 필요하므로, 컨트롤이 이 곳에 얹어두고 화면이 전사 · 저장 시점에 꺼내 쓴다.
 *
 * 행(KeyField)마다 하나씩 든다(2026-10-22). 예전에는 화면 단위 값 하나라, 저장 전에 다른 회의록을 누르면
 * 녹음이 사라졌다. 화면은 전사 · 저장 때 올린 뒤(dc_url 을 행 js_attach 에 씀) 그 행 값을 지운다.
 * 조회 · 화면 닫기 때 clearMeetingAudio() 로 전부 지운다.
 *
 * 미리듣기 URL — 다른 음성으로 바뀔 때와 전부 지울 때 푼다. 올린 뒤 한 행만 지울 때는 풀지 않는다
 * (녹음기가 같은 URL 로 계속 들려준다). 녹음기의 지우기 버튼은 녹음기가 직접 푼다.
 */
const pendingAudio = new Map<string, SnAudioValue>();

const revoke = (value?: SnAudioValue | null) => {
  if (value?.url) URL.revokeObjectURL(value.url);
};

export const setMeetingAudio = (rowKey: string, value: SnAudioValue | null) => {
  if (!rowKey) return;
  if (!value) {
    pendingAudio.delete(rowKey);
    return;
  }
  const prev = pendingAudio.get(rowKey);
  if (prev && prev !== value) revoke(prev);
  pendingAudio.set(rowKey, value);
};

export const getMeetingAudio = (rowKey: string): SnAudioValue | null =>
  (rowKey && pendingAudio.get(rowKey)) || null;

/** 아직 올리지 않은 음성이 있는 행 키 */
export const pendingMeetingAudioKeys = (): string[] =>
  Array.from(pendingAudio.keys());

export const clearMeetingAudio = () => {
  pendingAudio.forEach((value) => revoke(value));
  pendingAudio.clear();
};

/**
 * 목적: 전사 원문 보기 칸 ↔ 녹음기 신호 (2026-10-23).
 *
 * 배경:
 * 둘 다 메타 렌더러(SnFindFormContols)가 그리는 서로 다른 칸이라 props 로 이을 수 없다. 행 키(KeyField)를 달아 신호만 주고받는다.
 * - 재생 : 보기 칸에서 시각을 누르면 그 행의 녹음기가 그 시점부터 재생한다
 * - 보기 : 화면이 새로 전사하면 그 행의 보기 칸이 편집에서 보기로 돌아간다
 */
type SeekListener = (rowKey: string, seconds: number) => void;
const seekListeners = new Set<SeekListener>();

export const requestMeetingAudioSeek = (rowKey: string, seconds: number) => {
  seekListeners.forEach((listener) => listener(rowKey, seconds));
};

export const onMeetingAudioSeek = (listener: SeekListener) => {
  seekListeners.add(listener);
  return () => {
    seekListeners.delete(listener);
  };
};

type ViewListener = (rowKey: string) => void;
const viewListeners = new Set<ViewListener>();

export const requestTranscriptView = (rowKey: string) => {
  viewListeners.forEach((listener) => listener(rowKey));
};

export const onTranscriptView = (listener: ViewListener) => {
  viewListeners.add(listener);
  return () => {
    viewListeners.delete(listener);
  };
};

/**
 * 목적: 저장된 음성(js_attach의 dc_url)을 서버에서 받아오되, dc_url별로 캐시해 재사용한다.
 *
 * 배경:
 * 재생(SnAudioRecorder)과 전사(화면)가 이 함수를 공유하므로, 이미 재생하며 받아둔 blob은
 * 전사 시 재다운로드 없이 그대로 쓰인다(세션 동안 유지). 진행 중 요청도 합쳐 한 번만 서버에 간다.
 */
const savedBlobCache = new Map<string, Promise<Blob | null>>();

export const fetchSavedAudioBlob = async (
  dcUrl: string
): Promise<Blob | null> => {
  if (!dcUrl) return null;

  const cached = savedBlobCache.get(dcUrl);
  if (cached) return cached;

  const request = (async (): Promise<Blob | null> => {
    try {
      const res = await fetch("/snotes/file/download2", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
        },
        body: JSON.stringify({ dc_url: dcUrl }),
      });
      if (!res.ok) return null;
      return await res.blob();
    } catch (error) {
      return null;
    }
  })();

  savedBlobCache.set(dcUrl, request);
  const blob = await request;
  // 실패한 요청은 캐시에서 제거해 다음에 다시 시도할 수 있게 한다.
  if (!blob) savedBlobCache.delete(dcUrl);
  return blob;
};
